import {
  FindingType,
  FindingsByTooth,
  RenderableFindingBase,
} from "@kells/interfaces/finding";
import { REPORT_FINDING_BOX_STROKE } from "../constants/report-finding-palette";

export function filterFindingsBySelectedType(
  findings: RenderableFindingBase[],
  selectedFilter: string | null | undefined
): RenderableFindingBase[] {
  if (!selectedFilter) {
    return findings;
  }
  return findings.filter((finding) => finding.type === selectedFilter);
}

/**
 * When a tooth has multiple finding types, we pick the first match in this
 * order (clinical / UX priority for a single color).
 */
export const DOMINANT_FINDING_TYPE_ORDER: FindingType[] = [
  FindingType.Caries,
  FindingType.Infection,
  FindingType.Fracture,
  FindingType.MissingTooth,
  FindingType.BoneLoss,
  FindingType.DefectiveRestoration,
  FindingType.Calculus,
  FindingType.GumInflammation,
  FindingType.GumRecession,
  FindingType.Plaque,
  FindingType.Material,
  FindingType.Tooth,
  FindingType.Dummy,
];

export function getHexForFindingType(type: FindingType | string): string {
  const entry =
    REPORT_FINDING_BOX_STROKE[type as FindingType] ??
    REPORT_FINDING_BOX_STROKE[FindingType.Caries];
  return entry.outer;
}

/** Mix a hex with white for softer tooth interior fill. */
export function mixHexWithWhite(hex: string, whiteBlend: number): string {
  const h = hex.replace("#", "");
  if (h.length !== 6) {
    return hex;
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const t = Math.min(1, Math.max(0, whiteBlend));
  const m = (c: number) => Math.round(c + (255 - c) * t);
  const nr = m(r);
  const ng = m(g);
  const nb = m(b);
  return `#${[nr, ng, nb]
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("")}`;
}

/**
 * The finding type to show for tooth coloring. When a filter is active, the
 * tooth only shows that type's color if the tooth has that finding; otherwise
 * this returns null and the parent should use a muted "filtered out" look.
 */
export function getDisplayFindingType(
  f: FindingsByTooth | null | undefined,
  selectedFilter: string | null | undefined
): FindingType | null {
  if (!f?.allFindings?.length) {
    return null;
  }
  const types = new Set(f.allFindings.map((x) => x.type as string));
  if (selectedFilter) {
    if (types.has(selectedFilter)) {
      return selectedFilter as FindingType;
    }
    return null;
  }
  for (const t of DOMINANT_FINDING_TYPE_ORDER) {
    if (types.has(t)) {
      return t;
    }
  }
  return f.allFindings[0].type as FindingType;
}

export function uniqueFindingTypesFromByTooth(
  byTooth: Map<string, FindingsByTooth> | null | undefined
): FindingType[] {
  if (!byTooth?.size) {
    return [];
  }
  const out = new Set<FindingType>();
  byTooth.forEach((f) => {
    f.allFindings?.forEach((n) => out.add(n.type as FindingType));
  });
  const ordered: FindingType[] = [];
  DOMINANT_FINDING_TYPE_ORDER.forEach((t) => {
    if (out.has(t)) {
      ordered.push(t);
    }
  });
  return ordered;
}
